import { useLocation } from "react-router-dom";
import {
  Typography,
} from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { ContactDetailResponseDto } from "@shared/generated/crm/models/ContactDetailResponseDto";
import type { LeadResponseDto } from "@shared/generated/crm/models/LeadResponseDto";
import { INTENT_LABELS } from "../lead-formatters";
import { deriveContactName } from "../contacts-helpers";
import { capitalize } from "@shared/utils/text-formatter";
import { LeadCard } from "@shared/components/LeadCard";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  LeadAccordion,
  LeadAccordionDetails,
  LeadAccordionSummary,
} from "./LeadAccordion";
import { LeadDetailsContent } from "./LeadDetailsContent";

const LeadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SectionTitle = styled(Typography)`
  color: ${(p) => p.theme.text.colors.brand};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LeadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RelationshipLeadAccordion = styled(LeadAccordion)`
  .MuiAccordionSummary-root:hover {
    background-color: ${(p) => p.theme.colors.background5};
  }
`;

type CrmRelationshipOverviewProps = {
  contact: ContactDetailResponseDto;
  onLeadClick?: (lead: LeadResponseDto) => void;
};

export const CrmRelationshipOverview = ({
  contact,
  onLeadClick,
}: CrmRelationshipOverviewProps) => {
  const location = useLocation();

  const openLeads = (contact.leads ?? [])
    .filter((l) => !l.closedAt)
    .sort((a, b) => {
      const aTime = a.lastSignalAt ? new Date(a.lastSignalAt).getTime() : 0;
      const bTime = b.lastSignalAt ? new Date(b.lastSignalAt).getTime() : 0;
      return bTime - aTime;
    });
    
  if (openLeads.length === 0) return null;

  const resolveOpenLeadId = (): string | undefined => {
    const stateOpenLeadId = location.state instanceof Object && "openLeadId" in location.state && typeof location.state.openLeadId === "string" ? location.state.openLeadId : undefined;
    if (stateOpenLeadId && openLeads.some((l) => l.id === stateOpenLeadId)) {
      return stateOpenLeadId;
    }
    if (openLeads.length === 1) {
      return openLeads[0].id;
    }
    return undefined;
  };

  const openLeadId = resolveOpenLeadId();
  const contactName = deriveContactName(contact);

  return (
    <LeadSection>
      <SectionTitle variant="subtitle1">
        <BoltIcon fontSize="small" />
        Open CRM Leads
      </SectionTitle>
      <LeadList>
        {openLeads.map((lead) => (
          <RelationshipLeadAccordion
            key={lead.id}
            variant="card"
            disableGutters={true}
            defaultExpanded={openLeadId ? lead.id === openLeadId : false}
            onChange={(_, expanded) => {
              if (expanded) {
                onLeadClick?.(lead);
              }
            }}
          >
            <LeadAccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
              <LeadCard
                avatar={null}
                chips={[
                  {
                    label: "Contact Role",
                    value:
                      INTENT_LABELS[lead.intent.toUpperCase()] ?? lead.intent,
                  },
                  { label: "Stage", value: capitalize(lead.stage) },
                ]}
                padding="none"
                signal={lead.lastSignalAt ? `Last signal at ${new Date(lead.lastSignalAt).toLocaleString()}` : 'No signals yet'}
                variant="flat"
                shape="square"
              />
            </LeadAccordionSummary>
            <LeadAccordionDetails>
              <LeadDetailsContent lead={lead} contactName={contactName} />
            </LeadAccordionDetails>
          </RelationshipLeadAccordion>
        ))}
      </LeadList>
    </LeadSection>
  );
};
